import { __awaiter, __decorate } from "tslib";
import { Auth, CredentialManager } from '@genesislcap/foundation-comms';
import { EventEmitter } from '@genesislcap/foundation-events';
import { I18next } from '@genesislcap/foundation-i18n';
import { FASTElement, customElement, observable, attr } from '@microsoft/fast-element';
import { Route } from '@microsoft/fast-router';
import logo from '../assets/logo.svg';
import { HeaderConfig } from '../config/config';
import { logger } from '../utils';
import { MainStyles as styles } from './main.styles';
import { DynamicTemplate as template, LoadingTemplate, MainTemplate } from './main.template';
/**
 * Navigation micro-frontend includes navigation bar and flyout menu
 *
 * @remarks
 *
 * `foundation-header` micro-frontend can be added to the project to include a navigation bar
 * and flyout menu. There are multiple ways that the behaviour of the component can be configured
 * - the icon shown on the navigation bar and flyout menu (this shows the Genesis logo by default).
 * - navigation links at the left-hand side of the navigation bar.
 * - the control buttons on the right-hand side of the navigation bar can be shown or hidden, and their behaviour controlled via event listeners
 * - The contents of the flyout menu.
 *
 * @param logo-src - Option attribute which sets the source of the image in the navigation bar and flyout menu. The Genesis logo will be shown if this attribute is not provided.
 * @param logo-alt-text -Descriptive logo text.
 * @param show-luminance-toggle-button - Boolean attribute which controls whether the navigation bar will display the luminance toggle icon.
 * @param show-misc-toggle-button - Boolean attribute which controls whether the navigation bar will display the miscellaneous behaviour icon.
 * @param notification-icon-clicked - Boolean attribute which controls whether the navigation bar will display the show notification icon.
 * @param show-connection-indicator - Boolean attribute which controls whether the navigation bar will display the connection indicator.
 * @param show-language-selector - Boolean attribute which controls whether the navigation bar will display the language selector.
 * @param hide-side-bar - Boolean attribute which controls whether the navigation bar will display the side bar.
 *
 * @param userName - String which defines the username to be displayed in the navigation bar.
 * @param routeButtons - Array of objects which define the route buttons to be displayed in the navigation bar.
 * @param routeNavItems - Array of {@link @genesislcap/foundation-ui#FoundationRouteNavItem | FoundationRouteNavItems} which define the route buttons to be displayed in the navigation bar.
 * @param languageOptions - Object which defines the language options to be displayed in the language selector.
 *
 * @public
 *
 * @fires luminance-icon-clicked - Dispatched when the user clicks on the luminance toggle icon in the navigation bar.
 * @fires misc-icon-clicked - Dispatched when the user clicks on the miscellaneous behaviour icon in the navigation bar.
 * @fires notification-icon-clicked - Dispatched when the user clicks on the notification icon in the navigation bar.
 * @fires language-changed - Dispatched when the user changes the language in the language selector.
 *
 */
let Navigation = class Navigation extends EventEmitter(FASTElement) {
    constructor() {
        var _a;
        super(...arguments);
        this.ready = false;
        this.sideNavOpen = false;
        /**
         * Optional attribute which controls the icon to show on the navigation bar and flyout
         * Control via `logo-src`
         *
         * @remarks
         *
         * If this attribute is not set then the Genesis logo is shown instead
         */
        this.logoSrc = logo;
        this.logoAltText = 'Corporate Logo';
        /**
         * Boolean attribute which controls whether to show the connection indicator on the navigation bar
         * Control via `show-connection-indicator`
         *
         */
        this.showConnectionIndicator = true;
        /**
         * Boolean attribute which controls whether to show the language selector on the navigation bar
         * Control via `show-language-selector`
         *
         * @experimental
         * @remarks
         *
         * Requires `i18nextOptions` to be set
         */
        this.showLanguageSelector = false;
        this.languageOptions = {
            availableLanguages: Object.keys((_a = this.i18next.config.resources) !== null && _a !== void 0 ? _a : {}),
            selectedLanguage: this.i18next.config.lng,
        };
    }
    connectedCallback() {
        const _super = Object.create(null, {
            connectedCallback: { get: () => super.connectedCallback }
        });
        return __awaiter(this, void 0, void 0, function* () {
            _super.connectedCallback.call(this);
            logger.debug(`foundation-header is now connected to the DOM`);
            yield this.loadRemotes();
            this.userName = this.auth.loggedUserResult
                ? this.auth.loggedUserResult.username
                : 'Genesis User';
        });
    }
    /**
     * @internal
     */
    showNotificationsButtonChanged() {
        logger.deprecated('show-notification-button', 'Leave unset and add/remove PBC Notify instead.');
    }
    /**
     * @internal
     */
    routeButtonsChanged() {
        logger.deprecated('routeButtons', 'Use routeNavItems instead.');
        if (!this.routeNavItems) {
            this.routeNavItems = this.routeButtons.map((button) => ({
                title: button.title,
                routePath: button.path,
                placementIndex: button.index,
                icon: button.icon
                    ? {
                        name: button.icon,
                        variant: button.variant,
                    }
                    : undefined,
            }));
        }
    }
    /**
     * Load remote components
     *
     * @remarks
     *
     * With regards to module federation
     *
     * @internal
     */
    loadRemotes() {
        return __awaiter(this, void 0, void 0, function* () {
            const { registerCommonZeroComponents } = yield import('../components/zero-components');
            yield registerCommonZeroComponents();
            this.ready = true;
        });
    }
    selectTemplate() {
        return this.ready ? MainTemplate : LoadingTemplate;
    }
    /**
     * Logs the user out of their session
     *
     * @remarks
     *
     * Activated when the user clicks on the logout button on the right hand side of the navigation bar. Requires
     * the usage of the auth functionality from {@link @genesislcap/foundation-comms#Auth}.
     *
     * @public
     */
    logout() {
        return __awaiter(this, void 0, void 0, function* () {
            logger.debug('logout submission');
            try {
                const result = yield this.auth.logout();
                if (!result.success) {
                    logger.error(`Couldn't log out`, result);
                    return;
                }
                logger.debug('logged out', result);
                yield this.credentialManager.preventSilentAccess();
                this.navigateTo('/');
            }
            catch (err) {
                logger.error(`Error: ${err.message}`);
            }
        });
    }
    /**
     * Changes the route of the current page
     *
     * @remarks
     *
     * Requires setup of the Router and usage of the FAST Router
     *
     * @param path - string represeting the new route to move to
     *
     * @public
     */
    navigateTo(path) {
        try {
            logger.debug('navigateTo', path);
            Route.path.push(path);
        }
        catch (err) {
            logger.error(`Error: ${err.message}`);
        }
    }
    /**
     * @remarks
     * Allows nav items to appear conditionally based on the permissions held by the current user.
     *
     * @internal
     */
    isUserPermitted(permission) {
        return this.auth.currentUser.hasPermission(permission);
    }
    /**
     * Opens the flyout menu on the left hand side
     *
     * @remarks
     *
     * Activated when the user clicks on the menu button on the navigation
     * bar left hand side
     *
     * @internal
     */
    toggleNavVisibility() {
        this.sideNavOpen = !this.sideNavOpen;
    }
    /**
     * Explicitly closes the flyout menu on the left
     *
     * @privateRemarks
     * When the user tabs though and out of the flyout, the hamburger menu item will be re-focused and when that occurs we
     * can close the menu to return to normal flow.
     *
     * @internal
     */
    closeNav() {
        this.sideNavOpen = false;
    }
    /**
     * Emits the event corresponding to the luminance icon being clicked
     *
     * @remarks
     *
     * Activated when the user clicks on the moon icon on the navigation
     * bar right hand side
     *
     * @internal
     */
    luminanceIconEvent() {
        this.$emit('luminance-icon-clicked');
    }
    /**
     * Emits the event corresponding to the miscellaneous icon being clicked
     *
     * @remarks
     *
     * Activated when the user clicks on the menu icon on the navigation
     * bar right hand side
     *
     * @internal
     */
    miscIconEvent() {
        this.$emit('misc-icon-clicked');
    }
    /**
     * Emits the event corresponding to the notification icon being clicked
     *
     * @remarks
     *
     * Activated when the user clicks on the bell icon on the navigation
     * bar right hand side
     *
     * @internal
     */
    notificationIconEvent() {
        this.$emit('notification-icon-clicked');
    }
    /**
     * Changes the language of the application
     *
     * @remarks
     *
     * Activated when the user selects a language from the language selector that uses the I18next service.
     *
     * @experimental
     */
    changeLanguage(e) {
        this.$emit('language-changed', e.detail.value);
        this.i18next.changeLanguageTo(e.detail.value);
    }
    /**
     * To localised text
     * @remarks
     * Returns localised version of the text if available.
     * @experimental
     * @privateRemarks
     * Perhaps move things like showLanguageSelector into the config and just call i18next directly for translations.
     * Is languageOptions needed.
     */
    toLocalisedText(text) {
        return this.showLanguageSelector && this.languageOptions
            ? this.i18next.t(text, { lng: this.i18next.language })
            : text;
    }
};
__decorate([
    Auth
], Navigation.prototype, "auth", void 0);
__decorate([
    CredentialManager
], Navigation.prototype, "credentialManager", void 0);
__decorate([
    I18next
], Navigation.prototype, "i18next", void 0);
__decorate([
    HeaderConfig
], Navigation.prototype, "headerConfig", void 0);
__decorate([
    observable
], Navigation.prototype, "ready", void 0);
__decorate([
    observable
], Navigation.prototype, "userName", void 0);
__decorate([
    observable
], Navigation.prototype, "sideNavOpen", void 0);
__decorate([
    attr({ attribute: 'logo-src' })
], Navigation.prototype, "logoSrc", void 0);
__decorate([
    attr({ attribute: 'logo-alt-text' })
], Navigation.prototype, "logoAltText", void 0);
__decorate([
    attr({ mode: 'boolean', attribute: 'show-luminance-toggle-button' })
], Navigation.prototype, "showLuminanceToggleButton", void 0);
__decorate([
    attr({ mode: 'boolean', attribute: 'show-misc-toggle-button' })
], Navigation.prototype, "showMiscToggleButton", void 0);
__decorate([
    attr({ mode: 'boolean', attribute: 'show-notification-button' })
], Navigation.prototype, "showNotificationsButton", void 0);
__decorate([
    attr({ mode: 'boolean', attribute: 'show-connection-indicator' })
], Navigation.prototype, "showConnectionIndicator", void 0);
__decorate([
    attr({ mode: 'boolean', attribute: 'show-language-selector' })
], Navigation.prototype, "showLanguageSelector", void 0);
__decorate([
    observable
], Navigation.prototype, "languageOptions", void 0);
__decorate([
    attr({ mode: 'boolean', attribute: 'hide-side-bar' })
], Navigation.prototype, "hideSideBar", void 0);
__decorate([
    observable
], Navigation.prototype, "routeButtons", void 0);
__decorate([
    observable
], Navigation.prototype, "routeNavItems", void 0);
Navigation = __decorate([
    customElement({
        name: 'foundation-header',
        template,
        styles,
    })
], Navigation);
export { Navigation };
/**
 * FoundationHeader.
 *
 * @remarks
 * Base MF export used by host application `configure` calls. It does not set up a design system or components. Both are
 * expected to be registered in the host application, which should provide the details of which to the MF via
 * TemplateOptions.
 *
 * See the {@link configure} hook for more information on how to set up and use this micro frontend in your application.
 *
 * @privateRemarks
 * We will override loadRemotes to remove zero. This setup is the inverse of FoundationAuth.
 * We're doing this to avoid a breaking change, so consumers can continue to use Navigation which bundles zero.
 *
 * @public
 */
export class FoundationHeader extends Navigation {
    loadRemotes() {
        return __awaiter(this, void 0, void 0, function* () {
            this.ready = true;
        });
    }
}
/**
 * ZeroHeader is a zero version of the header micro-frontend.
 *
 * @remarks
 * A zero version that pre-registers zero components and uses the zero design system so that it can run standalone.
 * Generally this is used for development purposes, but can be deployed as a standalone application if required or be
 * used by a host application that is already using zero. ZeroHeader and its components won't bloat the core bundle as
 * these are only loaded on-demand when in use.
 *
 * @example
 * ```ts
 * import { ZeroHeader } from '@genesislcap/foundation-header';
 * ...
 * ZeroHeader
 * ```
 *
 * @example Load the micro frontend on-demand
 * ```ts
 *   const { ZeroHeader } = await import('@genesislcap/foundation-header');
 * },
 * ```
 *
 * @privateRemarks
 * For now we just extend Navigation as is, given that is ZeroHeader really.
 *
 * @public
 */
let ZeroHeader = class ZeroHeader extends Navigation {
};
ZeroHeader = __decorate([
    customElement({
        name: 'zero-header',
        template,
        styles,
    })
], ZeroHeader);
export { ZeroHeader };
